import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { getToken, API, formatApiError } from "@/lib/api";
import { RichText, RichView, htmlToText, isHtmlEmpty } from "@/components/RichText";
import { toast } from "sonner";
import { OUTCOMES } from "@/lib/assessmentData";
import { ReportsCatalogue } from "@/components/ReportsCatalogue";
import {
  Shield, Building2, Users, Layers, PlayCircle, LogOut, Plus, Trash2, Copy,
  Pencil, Upload, ChevronRight, Settings, Archive, X, FileDown, FileText, UserCog, AlertTriangle,
  Code2, FileBarChart2, BookOpen, Download, KeyRound, Mail, Eye, RotateCcw, Save, ClipboardCheck, Send,
} from "lucide-react";

const TABS = [
  { id: "companies", label: "Companies", icon: Building2 },
  { id: "roles", label: "Roles", icon: UserCog },
  { id: "scenarios", label: "Scenarios", icon: Layers },
  { id: "sessions", label: "Sessions", icon: PlayCircle },
  { id: "comms", label: "All Comms", icon: Mail },
  { id: "assessments", label: "Assessments", icon: ClipboardCheck },
];

const DEV_TABS = [
  { id: "dev-reports", label: "Admin reports", icon: FileBarChart2 },
  { id: "dev-architecture", label: "General architecture", icon: BookOpen },
];

const ALL_TAB_IDS = [...TABS, ...DEV_TABS].map((t) => t.id);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(ALL_TAB_IDS.includes(params.get("tab")) ? params.get("tab") : "companies");
  const dirtyRef = useRef(false);
  const handleCommsDirty = useCallback((d) => { dirtyRef.current = d; }, []);

  const switchTab = (id) => {
    if (id === tab) return;
    if (dirtyRef.current && !window.confirm("You have unsaved changes to this email template. Discard them?")) return;
    dirtyRef.current = false;
    setTab(id);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] text-white flex">
      {/* Sidebar */}
      <aside className="w-60 border-r border-white/10 bg-[var(--bg-surface)] flex flex-col p-4 sticky top-0 h-screen">
        <button onClick={() => nav("/")} data-testid="admin-home-btn" className="flex items-center gap-2 px-2 mb-8 hover:opacity-80 transition-opacity">
          <div className="w-7 h-7 bg-white flex items-center justify-center"><Shield className="w-4 h-4 text-black" /></div>
          <span className="font-display font-extrabold tracking-tight">TARDIGRADE</span>
        </button>
        <div className="overline px-2 mb-3" style={{ fontSize: "16px" }}>Admin console</div>
        <nav className="space-y-1 flex-1">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => switchTab(t.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-base transition-colors ${tab === t.id ? "bg-white text-black font-medium" : "text-[var(--txt-secondary)] hover:bg-white/5"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}

          <div className="pt-5">
            <div className="overline px-2 mb-3 flex items-center gap-2 text-[var(--txt-muted)]" style={{ fontSize: "13px" }}>
              <Code2 className="w-3.5 h-3.5" /> Developers
            </div>
            {DEV_TABS.map((t) => (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => switchTab(t.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-base transition-colors ${tab === t.id ? "bg-white text-black font-medium" : "text-[var(--txt-secondary)] hover:bg-white/5"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </nav>
        <div className="border-t border-white/10 pt-4 mt-4">
          <div className="px-2 mb-3 text-sm text-[var(--txt-muted)] font-mono truncate">{user?.email}</div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-base text-[var(--txt-secondary)] hover:bg-white/5 transition-colors">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {tab === "companies" && <CompaniesTab />}
        {tab === "roles" && <RolesTab />}
        {tab === "scenarios" && <ScenariosTab />}
        {tab === "sessions" && <SessionsTab />}
        {tab === "comms" && <CommsTab onDirtyChange={handleCommsDirty} />}
        {tab === "assessments" && <AssessmentsTab />}
        {tab === "dev-reports" && <DevReportsTab goTab={switchTab} />}
        {tab === "dev-architecture" && <GeneralArchitectureTab />}
      </main>
    </div>
  );
}

function Header({ title, sub, children }) {
  return (
    <div className="flex items-end justify-between border-b border-white/10 px-8 py-6 sticky top-0 glass z-20">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">{title}</h1>
        {sub && <p className="text-lg text-[var(--txt-muted)] mt-1">{sub}</p>}
      </div>
      <div className="flex gap-3">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------- Developers
function DevReportsTab({ goTab }) {
  return (
    <div data-testid="dev-reports-tab">
      <Header title="Admin reports" sub="Every report, export and document in Tardigrade: who it's for, what's in it and where to get it." />
      <ReportsCatalogue goTab={goTab} />
    </div>
  );
}

const DOCS_UPDATED = "v4 · includes self-assessment, resources & participant reports";

const ARCHITECTURE_DOCS = [
  {
    title: "Architecture diagrams",
    desc: "Visual data-flow diagram (React → FastAPI → MongoDB) and the full MongoDB entity-relationship diagram.",
    docId: "architecture",
    filename: "Tardigrade_Architecture_Diagrams.pdf",
    type: "PDF · 2 pages",
    icon: Layers,
    testid: "doc-architecture-diagrams",
  },
  {
    title: "Backend operations & data reference",
    desc: "Written operational description of all backend logic, endpoints, the session engine, security posture and every MongoDB collection.",
    docId: "backend-operations",
    filename: "Tardigrade_Backend_Operations.pdf",
    type: "PDF",
    icon: FileText,
    testid: "doc-backend-operations",
  },
];

function GeneralArchitectureTab() {
  const [busy, setBusy] = useState("");

  const fetchDoc = async (doc, download) => {
    setBusy(doc.docId + (download ? "-dl" : "-view"));
    try {
      const res = await api.get(`/dev/docs/${doc.docId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (download) {
        const a = document.createElement("a");
        a.href = url; a.download = doc.filename;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load document");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="dev-architecture-tab">
      <Header title="General architecture" sub="Reference documents for developers & technical stakeholders" />
      <div className="px-8 py-8 space-y-5 max-w-3xl">
        <p className="text-sm text-[var(--txt-muted)] flex items-center gap-2" data-testid="docs-access-note">
          <Shield className="w-3.5 h-3.5" /> These documents are served only to signed-in admins — they are not publicly accessible.
        </p>
        {ARCHITECTURE_DOCS.map((d) => (
          <div key={d.docId} data-testid={d.testid}
            className="elevated p-6 flex items-start gap-5 hover:bg-white/[0.03] transition-colors">
            <div className="w-11 h-11 shrink-0 bg-white/5 flex items-center justify-center">
              <d.icon className="w-5 h-5 text-[var(--txt-secondary)]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="font-display font-bold text-lg">{d.title}</h3>
                <span className="overline text-[var(--txt-muted)] font-mono" style={{ fontSize: "11px" }}>{d.type}</span>
              </div>
              <p className="text-base text-[var(--txt-secondary)]">{d.desc}</p>
              <p className="text-xs text-[var(--txt-muted)] font-mono mt-1" data-testid={`${d.testid}-updated`}>{DOCS_UPDATED}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => fetchDoc(d, false)} disabled={busy.startsWith(d.docId)}
                  data-testid={`${d.testid}-view`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-white/15 text-[var(--txt-secondary)] hover:bg-white/5 transition-colors disabled:opacity-50">
                  <BookOpen className="w-4 h-4" /> {busy === d.docId + "-view" ? "Opening…" : "View"}
                </button>
                <button onClick={() => fetchDoc(d, true)} disabled={busy.startsWith(d.docId)}
                  data-testid={`${d.testid}-download`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-white text-black font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
                  <Download className="w-4 h-4" /> {busy === d.docId + "-dl" ? "Preparing…" : "Download"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function Btn({ children, onClick, variant = "primary", testid, type = "button", className = "" }) {
  const styles = {
    primary: "bg-white text-black hover:bg-gray-200",
    ghost: "border border-white/20 hover:bg-white/10 text-white",
    danger: "border border-[var(--urgent)]/40 text-[var(--urgent)] hover:bg-[var(--urgent)]/10",
  };
  return <button type={type} data-testid={testid} onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-base font-medium transition-colors ${styles[variant]} ${className}`}>{children}</button>;
}

function Modal({ open, onClose, title, children, testid }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div data-testid={testid} className="grid-card w-full max-w-lg max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[var(--bg-surface)]">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-[var(--txt-muted)] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", testid, area }) {
  return (
    <label className="block">
      <span className="overline block mb-2" style={{ fontSize: "16px" }}>{label}</span>
      {area ? (
        <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={4}
          className="w-full elevated px-3 py-2.5 outline-none focus:border-white text-base" />
      ) : (
        <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full elevated px-3 py-2.5 outline-none focus:border-white text-base" />
      )}
    </label>
  );
}

const STATUS_COLORS = {
  draft: "text-[var(--txt-muted)] border-white/20",
  live: "text-[var(--success)] border-[var(--success)]/40",
  paused: "text-[var(--warning)] border-[var(--warning)]/40",
  ended: "text-[var(--txt-secondary)] border-white/20",
  archived: "text-[var(--urgent)] border-[var(--urgent)]/40",
};
function StatusPill({ status }) {
  return <span className={`inline-block px-2 py-0.5 text-sm font-mono uppercase border ${STATUS_COLORS[status] || ""}`}>{status}</span>;
}

/* ----------------------------- COMPANIES ----------------------------- */
function CompaniesTab() {
  const [companies, setCompanies] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", sector: "", size: "" });
  const [selected, setSelected] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (modal === "edit") await api.put(`/companies/${form.id}`, form);
      else await api.post("/companies", form);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/companies/${confirmDel.id}`);
      toast.success("Company and all its data deleted");
      setConfirmDel(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (selected) return <CompanyUsers company={selected} onBack={() => { setSelected(null); load(); }} />;

  const token = getToken();
  return (
    <div>
      <Header title="Companies" sub="Isolated client profiles, users and sessions.">
        <a data-testid="export-all-xlsx" href={`${API}/admin/export-all.xlsx?token=${token}`}
          className="flex items-center gap-2 px-4 py-2 text-base font-medium border border-white/20 hover:bg-white/10 text-white transition-colors">
          <FileDown className="w-4 h-4" /> Export all (Excel)
        </a>
        <a data-testid="export-all-pdf" href={`${API}/admin/export-all.pdf?token=${token}`}
          className="flex items-center gap-2 px-4 py-2 text-base font-medium border border-white/20 hover:bg-white/10 text-white transition-colors">
          <FileText className="w-4 h-4" /> Export all (PDF)
        </a>
        <Btn testid="new-company-btn" onClick={() => { setForm({ name: "", sector: "", size: "" }); setModal("new"); }}><Plus className="w-4 h-4" /> New company</Btn>
      </Header>
      <div className="p-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c) => (
            <div key={c.id} data-testid={`company-card-${c.id}`} className="grid-card p-5 group hover:bg-[var(--bg-elevated)] transition-colors">
              <div className="flex items-start justify-between mb-4">
                <Building2 className="w-6 h-6" />
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-company-${c.id}`} onClick={() => { setForm(c); setModal("edit"); }} className="p-1.5 hover:bg-white/10"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`delete-company-${c.id}`} onClick={() => setConfirmDel(c)} className="p-1.5 hover:bg-[var(--urgent)]/20 text-[var(--urgent)]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h3 className="font-display font-bold text-xl mb-1">{c.name}</h3>
              <p className="text-base text-[var(--txt-muted)] mb-4">{c.sector || "—"} · {c.size || "—"}</p>
              <div className="flex gap-4 text-sm font-mono text-[var(--txt-secondary)] mb-4">
                <span>{c.user_count} users</span><span>{c.session_count} sessions</span>
              </div>
              <button data-testid={`manage-users-${c.id}`} onClick={() => setSelected(c)} className="flex items-center gap-1 text-base hover:text-white text-[var(--txt-secondary)]">
                Manage users <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
          {companies.length === 0 && <p className="text-[var(--txt-muted)] col-span-full">No companies yet. Create your first.</p>}
        </div>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "edit" ? "Edit company" : "New company"} testid="company-modal">
        <Input label="Company name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="company-name-input" placeholder="Acme Corp" />
        <Input label="Sector" value={form.sector} onChange={(v) => setForm({ ...form, sector: v })} testid="company-sector-input" placeholder="Financial services" />
        <Input label="Size" value={form.size} onChange={(v) => setForm({ ...form, size: v })} testid="company-size-input" placeholder="500–1000 employees" />
        <Btn testid="save-company-btn" onClick={save}>Save company</Btn>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Delete company?" testid="delete-company-modal">
        {confirmDel && <>
          <div className="flex items-start gap-3 elevated p-3 border-l-2 border-l-[var(--urgent)]">
            <AlertTriangle className="w-5 h-5 text-[var(--urgent)] shrink-0 mt-0.5" />
            <p className="text-base text-[var(--txt-secondary)]">
              You are about to <b className="text-white">permanently delete</b> <b className="text-white">{confirmDel.name}</b>. This cannot be undone.
            </p>
          </div>
          <div className="text-base">
            <p className="mb-2 text-[var(--txt-secondary)]">This will wipe:</p>
            <ul className="space-y-1 font-mono text-base">
              <li>• <b className="text-white">{confirmDel.user_count}</b> users / login accounts</li>
              <li>• <b className="text-white">{confirmDel.session_count}</b> sessions (incl. archived)</li>
              <li>• all responses, notes, inbox messages & questionnaire answers</li>
            </ul>
          </div>
          <div className="elevated p-3">
            <p className="overline mb-2" style={{ fontSize: "16px" }}>Export this data first</p>
            <div className="flex gap-2">
              <a data-testid="confirm-export-xlsx" href={`${API}/admin/export-all.xlsx?token=${getToken()}`}
                className="flex items-center gap-2 px-3 py-2 text-base border border-white/20 hover:bg-white/10 transition-colors"><FileDown className="w-4 h-4" /> Excel</a>
              <a data-testid="confirm-export-pdf" href={`${API}/admin/export-all.pdf?token=${getToken()}`}
                className="flex items-center gap-2 px-3 py-2 text-base border border-white/20 hover:bg-white/10 transition-colors"><FileText className="w-4 h-4" /> PDF</a>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <Btn variant="ghost" testid="cancel-delete-company-btn" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            <Btn variant="danger" testid="confirm-delete-company-btn" onClick={doDelete}><Trash2 className="w-4 h-4" /> Delete permanently</Btn>
          </div>
        </>}
      </Modal>
    </div>
  );
}

function CompanyUsers({ company, onBack }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(null);
  const [emails, setEmails] = useState("");
  const [uploadRole, setUploadRole] = useState("Operational lead / manager");
  const [created, setCreated] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busyWelcome, setBusyWelcome] = useState(null);

  const load = useCallback(() => {
    api.get(`/companies/${company.id}/users`).then((r) => setUsers(r.data)).catch(() => {});
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  }, [company.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const list = emails.split(/\n+/).map((l) => l.trim()).filter(Boolean).map((line) => {
      const parts = line.split(/[,;]+/).map((p) => p.trim()).filter(Boolean);
      const email = (parts.find((p) => p.includes("@")) || "").toLowerCase();
      const name = parts.filter((p) => p !== (parts.find((x) => x.includes("@")) || "")).join(" ").trim();
      return { name, email };
    }).filter((a) => a.email);
    if (list.length === 0) { toast.error("Add at least one 'Name, email' line"); return; }
    try {
      const { data } = await api.post(`/companies/${company.id}/users`, { attendees: list, role: uploadRole });
      setCreated(data.created); setEmails(""); load();
      toast.success(`${data.created.length} attendee(s) created`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    await api.put(`/users/${edit.id}`, {
      name: edit.name, sim_role: edit.sim_role, role_description: edit.role_description,
      table_number: parseInt(edit.table_number) || 1,
    });
    toast.success("User updated"); setEdit(null); load();
  };
  const del = async (id) => { if (!window.confirm("Delete user?")) return; await api.delete(`/users/${id}`); load(); };
  const resetPwd = async (u) => {
    if (!window.confirm(`Generate a new password for ${u.email}? Their current password will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      toast.success(`New password for ${data.email}: ${data.password}`, { duration: 12000 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendWelcome = async (u) => {
    if (busyWelcome) return;
    if (!window.confirm(`Send the Welcome & credentials email to ${u.email} from noreply@tardigradeteam.com?`)) return;
    setBusyWelcome(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/send-welcome`);
      if (data.dev_mode) toast.message(`Dev mode — Welcome email logged for ${u.email} (not sent). Add Mailjet keys to send for real.`, { duration: 8000 });
      else if (data.delivered) toast.success(`Welcome email sent to ${u.email}`);
      else toast.error(`Could not send: ${data.error || "provider error"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyWelcome(null); }
  };

  return (
    <div>
      <Header title={company.name} sub="Attendees, roles and credentials.">
        <Btn variant="ghost" testid="back-companies-btn" onClick={onBack}>← Companies</Btn>
        <Btn testid="upload-users-btn" onClick={() => { setCreated([]); setModal("upload"); }}><Upload className="w-4 h-4" /> Add attendees</Btn>
      </Header>
      <div className="p-8">
        <table className="w-full text-base">
          <thead><tr className="text-left overline border-b border-white/10" style={{ fontSize: "16px" }}>
            <th className="py-3">Name</th><th>Email</th><th>Role</th><th>Table</th><th>Temp password</th><th></th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-3 font-medium">{u.name}</td>
                <td className="font-mono text-[var(--txt-secondary)]">{u.email}</td>
                <td>{u.sim_role}</td>
                <td className="font-mono">{u.table_number}</td>
                <td className="font-mono text-[var(--warning)]">{u.temp_password || "—"}</td>
                <td className="text-right">
                  <button data-testid={`send-welcome-${u.id}`} onClick={() => sendWelcome(u)} disabled={busyWelcome === u.id} title="Send welcome & credentials email" className="p-1.5 hover:bg-white/10 inline-flex disabled:opacity-40"><Mail className="w-3.5 h-3.5" /></button>
                  <button data-testid={`reset-pwd-${u.id}`} onClick={() => resetPwd(u)} title="Reset password" className="p-1.5 hover:bg-white/10 inline-flex"><KeyRound className="w-3.5 h-3.5" /></button>
                  <button data-testid={`edit-user-${u.id}`} onClick={() => setEdit(u)} className="p-1.5 hover:bg-white/10 inline-flex"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)} className="p-1.5 hover:bg-[var(--urgent)]/20 text-[var(--urgent)] inline-flex"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="py-8 text-[var(--txt-muted)]">No attendees yet. Add attendees with their name and email.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={modal === "upload"} onClose={() => setModal(null)} title="Add attendees" testid="upload-modal">
        <Input label="One attendee per line as: Name, email@example.com" value={emails} onChange={setEmails} area testid="emails-input" placeholder="Karlie Webb, karlie@acme.com&#10;Bob Smith, bob@acme.com" />
        <p className="text-sm text-[var(--txt-muted)] -mt-2">The name is used to greet each person in their emails (e.g. "Hi Karlie"). If a line has no name, we'll use the part before the @.</p>
        <label className="block">
          <span className="overline block mb-2" style={{ fontSize: "16px" }}>Assign role</span>
          <select data-testid="role-select" value={uploadRole} onChange={(e) => setUploadRole(e.target.value)} className="w-full elevated px-3 py-2.5 text-base outline-none">
            {roles.map((r) => <option key={r.id} value={r.name} className="bg-[var(--bg-elevated)]">{r.name}</option>)}
          </select>
        </label>
        <Btn testid="confirm-upload-btn" onClick={upload}>Create users</Btn>
        {created.length > 0 && (
          <div className="elevated p-3 text-sm font-mono space-y-1">
            <div className="overline mb-1">Generated credentials</div>
            {created.map((c) => <div key={c.email}>{c.name} — {c.email} — <span className="text-[var(--warning)]">{c.password}</span></div>)}
          </div>
        )}
      </Modal>

      <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit user & role" testid="edit-user-modal">
        {edit && <>
          <Input label="Name" value={edit.name || ""} onChange={(v) => setEdit({ ...edit, name: v })} testid="edit-name-input" />
          <label className="block">
            <span className="overline block mb-2" style={{ fontSize: "16px" }}>Primary role</span>
            <select data-testid="edit-role-select" value={edit.sim_role} onChange={(e) => { const name = e.target.value; const role = roles.find((r) => r.name === name); setEdit({ ...edit, sim_role: name, role_description: edit.role_description ? edit.role_description : (role?.description || "") }); }} className="w-full elevated px-3 py-2.5 text-base outline-none">
              {roles.map((r) => <option key={r.id} value={r.name} className="bg-[var(--bg-elevated)]">{r.name}</option>)}
            </select>
          </label>
          <Input label="Role description (reflect dual responsibilities here)" value={edit.role_description || ""} onChange={(v) => setEdit({ ...edit, role_description: v })} area testid="edit-desc-input" />
          <Input label="Table / team number" type="number" value={edit.table_number} onChange={(v) => setEdit({ ...edit, table_number: v })} testid="edit-table-input" />
          <Btn testid="save-user-btn" onClick={saveEdit}>Save</Btn>
        </>}
      </Modal>
    </div>
  );
}

/* ----------------------------- ROLES ----------------------------- */
function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Role name required");
    try {
      if (modal === "edit") await api.put(`/roles/${form.id}`, { name: form.name, description: form.description });
      else await api.post("/roles", { name: form.name, description: form.description });
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/roles/${confirmDel.id}`); toast.success("Role deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <Header title="Roles" sub="Define roles and attributes — available when assigning users to companies.">
        <Btn testid="new-role-btn" onClick={() => { setForm({ name: "", description: "" }); setModal("new"); }}><Plus className="w-4 h-4" /> New role</Btn>
      </Header>
      <div className="p-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((r) => (
            <div key={r.id} data-testid={`role-card-${r.id}`} className="grid-card p-5 group hover:bg-[var(--bg-elevated)] transition-colors">
              <div className="flex items-start justify-between mb-3">
                <UserCog className="w-6 h-6" />
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-role-${r.id}`} onClick={() => { setForm(r); setModal("edit"); }} className="p-1.5 hover:bg-white/10"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`delete-role-${r.id}`} onClick={() => setConfirmDel(r)} className="p-1.5 hover:bg-[var(--urgent)]/20 text-[var(--urgent)]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h3 className="font-display font-bold text-xl mb-2">{r.name}</h3>
              <p className="text-base text-[var(--txt-muted)] whitespace-pre-wrap">{r.description || "No attributes / description set."}</p>
            </div>
          ))}
          {roles.length === 0 && <p className="text-[var(--txt-muted)] col-span-full">No roles yet. Create your first.</p>}
        </div>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "edit" ? "Edit role" : "New role"} testid="role-modal">
        <Input label="Role name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="role-name-input" placeholder="Cyber security lead" />
        <Input label="Attributes / description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} area testid="role-desc-input" placeholder="Responsibilities, expectations, default remit for this role…" />
        <Btn testid="save-role-btn" onClick={save}>Save role</Btn>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Delete role?" testid="delete-role-modal">
        {confirmDel && <>
          <p className="text-base text-[var(--txt-secondary)]">Delete the role <b className="text-white">{confirmDel.name}</b>? This removes it from the selectable list. Users already assigned this role keep their current label until you change them.</p>
          <div className="flex gap-3 pt-1">
            <Btn variant="ghost" testid="cancel-delete-role-btn" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            <Btn variant="danger" testid="confirm-delete-role-btn" onClick={doDelete}><Trash2 className="w-4 h-4" /> Delete role</Btn>
          </div>
        </>}
      </Modal>
    </div>
  );
}

/* ----------------------------- ALL COMMS (email templates) ----------------------------- */
function CommsTab({ onDirtyChange }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testModal, setTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [guideModal, setGuideModal] = useState(false);
  const [guide, setGuide] = useState({ to: "", first_name: "", company_name: "" });
  const [sendingGuide, setSendingGuide] = useState(false);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const baseRef = useRef({ subject: "", body: "" });
  const hydratingRef = useRef(false);
  const recompute = (next) => setDirty(next.subject !== baseRef.current.subject || next.body !== baseRef.current.body);
  const editSubject = (v) => { const next = { ...draft, subject: v }; setDraft(next); recompute(next); };
  const editBody = (v) => {
    const next = { ...draft, body: v };
    setDraft(next);
    if (hydratingRef.current) { hydratingRef.current = false; baseRef.current = { ...baseRef.current, body: v }; }
    recompute(next);
  };

  const load = useCallback(() => api.get("/comms/templates").then((r) => {
    setTemplates(r.data);
    setActiveKey((k) => k || (r.data[0] && r.data[0].key));
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const active = templates.find((t) => t.key === activeKey) || null;

  useEffect(() => {
    if (active) {
      hydratingRef.current = true;
      baseRef.current = { subject: active.subject, body: active.body };
      setDraft({ subject: active.subject, body: active.body });
      setDirty(false);
    }
  }, [activeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTemplate = (key) => {
    if (dirty && !window.confirm("Discard unsaved changes to this template?")) return;
    setActiveKey(key);
  };

  const save = async () => {
    if (!active) return;
    if (!draft.subject.trim()) return toast.error("Subject is required");
    if (isHtmlEmpty(draft.body)) return toast.error("Email body is required");
    setSaving(true);
    try {
      const r = await api.put(`/comms/templates/${active.key}`, { subject: draft.subject, body: draft.body });
      setTemplates((ts) => ts.map((t) => (t.key === r.data.key ? r.data : t)));
      baseRef.current = { subject: r.data.subject, body: draft.body };
      setDirty(false);
      toast.success("Template saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!active) return;
    if (!window.confirm(`Reset "${active.name}" to the default copy? This overwrites your edits.`)) return;
    try {
      const r = await api.post(`/comms/templates/${active.key}/reset`);
      setTemplates((ts) => ts.map((t) => (t.key === r.data.key ? r.data : t)));
      hydratingRef.current = true;
      baseRef.current = { subject: r.data.subject, body: r.data.body };
      setDraft({ subject: r.data.subject, body: r.data.body }); setDirty(false);
      toast.success("Reset to default");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doPreview = async () => {
    if (!active) return;
    try {
      const r = await api.post(`/comms/templates/${active.key}/preview`, { subject: draft.subject, body: draft.body });
      setPreview(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyToken = async (token) => {
    try { await navigator.clipboard.writeText(token); toast.success(`Copied ${token} — paste into the subject or body`); }
    catch { toast.error("Couldn't copy — select and copy the field manually"); }
  };

  const doSendTest = async () => {
    if (sendingTest) return;
    const to = (testEmail || "").trim();
    if (!to) return toast.error("Enter a recipient email address");
    setSendingTest(true);
    try {
      const { data } = await api.post(`/comms/templates/${active.key}/send-test`, { to });
      if (data.dev_mode) toast.message(`Dev mode — test logged (not actually sent). Add Mailjet keys to send for real.`, { duration: 8000 });
      else if (data.delivered) toast.success(`Test email sent to ${to} from ${data.from}`);
      else toast.error(`Could not send: ${data.error || "provider error"}`);
      setTestModal(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingTest(false); }
  };

  const doSendGuide = async () => {
    if (sendingGuide) return;
    if (dirty) return toast.error("Save your changes before sending");
    if (!guide.to.trim()) return toast.error("Enter a recipient email address");
    setSendingGuide(true);
    try {
      const { data } = await api.post("/comms/templates/support_guide/send", { ...guide, to: guide.to.trim() });
      if (data.dev_mode) toast.message("Dev mode — email logged (not actually sent).", { duration: 8000 });
      else if (data.delivered) { toast.success(`Support guide sent to ${data.email}`); setGuideModal(false); }
      else toast.error(`Could not send: ${data.error || "provider error"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingGuide(false); }
  };

  return (
    <div>
      <Header title="All Comms" sub="Edit the emails participants receive. Merge fields in {{double braces}} are filled in automatically when an email is sent." />
      <div className="p-8 grid lg:grid-cols-[280px_1fr] gap-6 items-start">
        {/* Template list */}
        <div className="space-y-2" data-testid="comms-template-list">
          {templates.map((t) => (
            <button key={t.key} data-testid={`comms-template-${t.key}`} onClick={() => selectTemplate(t.key)}
              className={`w-full text-left grid-card p-4 transition-colors ${t.key === activeKey ? "border-white bg-[var(--bg-elevated)]" : "hover:bg-white/5"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-display font-bold text-base">{t.name}</span>
                {!t.editable && <span className="text-xs font-mono uppercase px-1.5 py-0.5 border border-white/20 text-[var(--txt-muted)]">system</span>}
              </div>
              <p className="text-sm text-[var(--txt-muted)] leading-snug">{t.description}</p>
            </button>
          ))}
          {templates.length === 0 && <p className="text-[var(--txt-muted)]">No templates.</p>}
        </div>

        {/* Editor */}
        {active && (
          <div data-testid="comms-editor" className="grid-card p-6 space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-display font-bold text-xl">{active.name}</h3>
                <p className="text-sm text-[var(--txt-muted)] mt-1 max-w-xl">{active.description}</p>
                <p className="text-sm text-[var(--txt-muted)] mt-2">Sends from <span className="font-mono text-[var(--txt-secondary)]" data-testid="comms-from-email">{active.from_email}</span></p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {active.key === "support_guide" && <Btn testid="comms-send-guide-btn" onClick={() => { setGuide({ to: "", first_name: "", company_name: "" }); setGuideModal(true); }}><Send className="w-4 h-4" /> Send to client</Btn>}
                <Btn variant="ghost" testid="comms-sendtest-btn" onClick={() => { setTestEmail(user?.email || ""); setTestModal(true); }}><Mail className="w-4 h-4" /> Send test</Btn>
                <Btn variant="ghost" testid="comms-preview-btn" onClick={doPreview}><Eye className="w-4 h-4" /> Preview</Btn>
                {active.editable && <Btn variant="ghost" testid="comms-reset-btn" onClick={reset}><RotateCcw className="w-4 h-4" /> Reset</Btn>}
                {active.editable && <Btn testid="comms-save-btn" onClick={save}><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</Btn>}
              </div>
            </div>

            {!active.editable && (
              <div className="flex items-start gap-2 text-sm text-[var(--warning)] border border-[var(--warning)]/30 bg-[var(--warning)]/5 px-3 py-2.5">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>This is a system email sent automatically. The copy is fixed and shown here for reference only.</span>
              </div>
            )}

            <label className="block">
              <span className="overline block mb-2" style={{ fontSize: "16px" }}>Subject line</span>
              <input data-testid="comms-subject-input" value={draft.subject} disabled={!active.editable}
                onChange={(e) => editSubject(e.target.value)}
                className="w-full elevated px-3 py-2.5 outline-none focus:border-white text-base disabled:opacity-70" />
            </label>

            <div>
              <span className="overline block mb-2" style={{ fontSize: "16px" }}>Email body</span>
              <RichText testid="comms-body" value={draft.body} disabled={!active.editable}
                onChange={editBody}
                placeholder="Write the email copy here…" minHeight={220} />
            </div>

            <div>
              <span className="overline block mb-2" style={{ fontSize: "16px" }}>Merge fields — click to copy, then paste into the subject or body</span>
              <div className="flex flex-wrap gap-2" data-testid="comms-merge-fields">
                {active.merge_fields.map((f) => (
                  <button key={f.token} type="button" data-testid={`comms-field-${f.token}`} title={f.label}
                    onClick={() => copyToken(`{{${f.token}}}`)}
                    className="font-mono text-sm px-2.5 py-1 border border-white/20 hover:border-white hover:bg-white/10 transition-colors">
                    {`{{${f.token}}}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal open={!!preview} onClose={() => setPreview(null)} title="Email preview" testid="comms-preview-modal">
        {preview && <>
          <p className="text-sm text-[var(--txt-muted)]">Rendered with sample data so you can see how merge fields appear.</p>
          <div className="elevated px-3 py-2.5">
            <span className="overline block mb-1" style={{ fontSize: "13px" }}>Subject</span>
            <p data-testid="comms-preview-subject" className="text-base font-medium">{preview.subject}</p>
          </div>
          <div className="elevated px-4 py-4 bg-white text-black" data-testid="comms-preview-body">
            <RichView html={preview.body} className="!text-black" />
          </div>
        </>}
      </Modal>

      <Modal open={testModal} onClose={() => setTestModal(false)} title="Send a test email" testid="comms-sendtest-modal">
        <p className="text-sm text-[var(--txt-muted)]">Sends this template (with sample data) from <span className="font-mono">{active?.from_email}</span> so you can check how it looks in a real inbox.</p>
        <label className="block">
          <span className="overline block mb-2" style={{ fontSize: "16px" }}>Recipient email</span>
          <input data-testid="comms-test-email-input" type="email" value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com"
            className="w-full elevated px-3 py-2.5 outline-none focus:border-white text-base" />
        </label>
        <Btn testid="comms-sendtest-confirm-btn" onClick={doSendTest}><Mail className="w-4 h-4" /> {sendingTest ? "Sending…" : "Send test"}</Btn>
      </Modal>

      <Modal open={guideModal} onClose={() => setGuideModal(false)} title="Send support guide" testid="comms-send-guide-modal">
        <p className="text-sm text-[var(--txt-muted)]">Sends the saved support guide from <span className="font-mono">{active?.from_email}</span>. First name and organisation fill the merge fields.</p>
        <Input label="Recipient email" type="email" value={guide.to} onChange={(v) => setGuide({ ...guide, to: v })} testid="guide-email-input" placeholder="contact@client.org" />
        <Input label="First name" value={guide.first_name} onChange={(v) => setGuide({ ...guide, first_name: v })} testid="guide-name-input" />
        <Input label="Organisation" value={guide.company_name} onChange={(v) => setGuide({ ...guide, company_name: v })} testid="guide-org-input" />
        <Btn testid="guide-send-confirm-btn" onClick={doSendGuide}><Send className="w-4 h-4" /> {sendingGuide ? "Sending…" : "Send guide"}</Btn>
      </Modal>
    </div>
  );
}


/* ----------------------------- SCENARIOS ----------------------------- */
function ScenariosTab() {
  const [scenarios, setScenarios] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ title: "", type: "Cyber attack", description: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => api.get("/scenarios").then((r) => setScenarios(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (modal === "edit") await api.put(`/scenarios/${form.id}`, form);
      else await api.post("/scenarios", { ...form, is_template: true });
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clone = async (id) => {
    try { await api.post(`/scenarios/${id}/clone`); toast.success("Cloned"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete scenario and its injects?")) return;
    try { await api.delete(`/scenarios/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (selected) return <InjectEditor scenario={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div>
      <Header title="Scenarios" sub="Base templates you can clone, edit and adapt per company.">
        <Btn testid="new-scenario-btn" onClick={() => { setForm({ title: "", type: "Cyber attack", description: "" }); setModal("new"); }}><Plus className="w-4 h-4" /> New scenario</Btn>
      </Header>
      <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scenarios.map((s) => (
          <div key={s.id} data-testid={`scenario-card-${s.id}`} className="grid-card p-5 group hover:bg-[var(--bg-elevated)] transition-colors">
            <div className="flex items-start justify-between mb-3">
              <span className="px-2 py-0.5 text-sm font-mono uppercase border border-white/20">{s.type}</span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`clone-scenario-${s.id}`} onClick={() => clone(s.id)} className="p-1.5 hover:bg-white/10"><Copy className="w-3.5 h-3.5" /></button>
                <button data-testid={`edit-scenario-${s.id}`} onClick={() => { setForm(s); setModal("edit"); }} className="p-1.5 hover:bg-white/10"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`delete-scenario-${s.id}`} onClick={() => del(s.id)} className="p-1.5 hover:bg-[var(--urgent)]/20 text-[var(--urgent)]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <h3 className="font-display font-bold text-xl mb-2">{s.title}</h3>
            <p className="text-base text-[var(--txt-muted)] mb-4 line-clamp-2">{s.description}</p>
            <button data-testid={`manage-injects-${s.id}`} onClick={() => setSelected(s)} className="flex items-center gap-1 text-base text-[var(--txt-secondary)] hover:text-white">
              {s.inject_count} injects · Edit <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "edit" ? "Edit scenario" : "New scenario"} testid="scenario-modal">
        <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="scenario-title-input" />
        <label className="block">
          <span className="overline block mb-2" style={{ fontSize: "16px" }}>Type</span>
          <select data-testid="scenario-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full elevated px-3 py-2.5 text-base outline-none">
            {["Cyber attack", "War / civil unrest", "Major natural disaster"].map((t) => <option key={t} className="bg-[var(--bg-elevated)]">{t}</option>)}
          </select>
        </label>
        <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} area testid="scenario-desc-input" />
        <Btn testid="save-scenario-btn" onClick={save}>Save</Btn>
      </Modal>
    </div>
  );
}

function InjectEditor({ scenario, onBack }) {
  const [injects, setInjects] = useState([]);
  const [edit, setEdit] = useState(null);
  const blank = { title: "", body: "", task: "", expected_phrases: "", checklist: "", clash_groups: "", default_seconds: 300 };

  const load = useCallback(() => api.get(`/scenarios/${scenario.id}/injects`).then((r) => setInjects(r.data)).catch(() => {}), [scenario.id]);
  useEffect(() => { load(); }, [load]);

  const open = (inj) => setEdit(inj ? {
    ...inj, expected_phrases: (inj.expected_phrases || []).join(", "), checklist: (inj.checklist || []).join(", "),
    clash_groups: (inj.clash_groups || []).map((g) => g.join(", ")).join("\n"),
  } : { ...blank });

  const save = async () => {
    const payload = {
      title: edit.title, body: edit.body, task: edit.task || "", default_seconds: parseInt(edit.default_seconds) || 300,
      expected_phrases: edit.expected_phrases.split(",").map((s) => s.trim()).filter(Boolean),
      checklist: edit.checklist.split(",").map((s) => s.trim()).filter(Boolean),
      clash_groups: (edit.clash_groups || "").split("\n")
        .map((line) => line.split(",").map((s) => s.trim()).filter(Boolean))
        .filter((g) => g.length >= 2),
    };
    try {
      if (edit.id) await api.put(`/injects/${edit.id}`, payload);
      else await api.post(`/scenarios/${scenario.id}/injects`, payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clone = async (id) => {
    try { await api.post(`/injects/${id}/clone`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete inject?")) return;
    try { await api.delete(`/injects/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <Header title={scenario.title} sub="Ordered injects · expected phrases & checklists power the analysis.">
        <Btn variant="ghost" testid="back-scenarios-btn" onClick={onBack}>← Scenarios</Btn>
        <Btn testid="new-inject-btn" onClick={() => open(null)}><Plus className="w-4 h-4" /> Add inject</Btn>
      </Header>
      <div className="p-8 space-y-3 max-w-4xl">
        {injects.map((inj, i) => (
          <div key={inj.id} data-testid={`inject-row-${inj.id}`} className="grid-card p-5 group">
            <div className="flex items-start justify-between">
              <div className="flex gap-4">
                <span className="font-mono text-[var(--urgent)] text-lg">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display font-bold">{inj.title}</h3>
                  <p className="text-base text-[var(--txt-muted)] mt-1 line-clamp-2 max-w-xl">{htmlToText(inj.body)}</p>
                  <div className="flex gap-3 mt-2 text-sm font-mono text-[var(--txt-secondary)]">
                    <span>{inj.default_seconds}s</span>
                    <span>{(inj.expected_phrases || []).length} phrases</span>
                    <span>{(inj.checklist || []).length} checklist</span>
                    {(inj.clash_groups || []).length > 0 && <span className="text-[var(--warning)]">{(inj.clash_groups || []).length} clash{(inj.clash_groups || []).length > 1 ? "es" : ""}</span>}
                    {!isHtmlEmpty(inj.task) && <span className="text-[var(--warning)]">task set</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`clone-inject-${inj.id}`} onClick={() => clone(inj.id)} className="p-1.5 hover:bg-white/10"><Copy className="w-3.5 h-3.5" /></button>
                <button data-testid={`edit-inject-${inj.id}`} onClick={() => open(inj)} className="p-1.5 hover:bg-white/10"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`delete-inject-${inj.id}`} onClick={() => del(inj.id)} className="p-1.5 hover:bg-[var(--urgent)]/20 text-[var(--urgent)]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {injects.length === 0 && <p className="text-[var(--txt-muted)]">No injects yet.</p>}
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Edit inject" : "New inject"} testid="inject-modal">
        {edit && <>
          <Input label="Title" value={edit.title} onChange={(v) => setEdit({ ...edit, title: v })} testid="inject-title-input" />
          <div className="block">
            <span className="overline block mb-2" style={{ fontSize: "16px" }}>Inject text (situation report)</span>
            <RichText value={edit.body} onChange={(v) => setEdit({ ...edit, body: v })} testid="inject-body" placeholder="Describe the situation the roles are facing…" />
          </div>
          <div className="block">
            <span className="overline block mb-2" style={{ fontSize: "16px" }}>Task / prompt <span className="text-[var(--txt-muted)] normal-case font-normal">(shown above the response box — structure it freely)</span></span>
            <RichText value={edit.task} onChange={(v) => setEdit({ ...edit, task: v })} testid="inject-task" placeholder={"1. What are your immediate actions?\n2. How do you coordinate with the wider department?"} />
          </div>
          <Input label="Expected phrases / keywords (comma separated)" value={edit.expected_phrases} onChange={(v) => setEdit({ ...edit, expected_phrases: v })} testid="inject-phrases-input" placeholder="isolate, notify, incident response" />
          <Input label="Checklist of expected actions (comma separated)" value={edit.checklist} onChange={(v) => setEdit({ ...edit, checklist: v })} testid="inject-checklist-input" placeholder="Declare incident, Notify CISO" />
          <label className="block">
            <span className="overline block mb-2" style={{ fontSize: "16px" }}>Clash groups <span className="text-[var(--txt-muted)] normal-case font-normal">(admin analysis only)</span></span>
            <p className="text-sm text-[var(--txt-muted)] mb-2">One group per line; comma-separate the competing terms. A clash is flagged (amber) in Run analysis when 2+ terms from a line appear across role responses — i.e. contradictory decisions.</p>
            <textarea data-testid="inject-clash-input" value={edit.clash_groups || ""} onChange={(e) => setEdit({ ...edit, clash_groups: e.target.value })} rows={3}
              placeholder={"pay ransom, do not pay\nevacuate, shelter in place"} className="w-full elevated px-3 py-2.5 text-base outline-none font-mono" />
          </label>
          <Input label="Default timer (seconds)" type="number" value={edit.default_seconds} onChange={(v) => setEdit({ ...edit, default_seconds: v })} testid="inject-seconds-input" />
          <Btn testid="save-inject-btn" onClick={save}>Save inject</Btn>
        </>}
      </Modal>
    </div>
  );
}

/* ----------------------------- SESSIONS ----------------------------- */
/* ----------------------------- ASSESSMENTS ----------------------------- */
const OUTCOME_STYLES = {
  not_suitable: "border-white/30 text-[var(--txt-secondary)]",
  good_candidate: "border-emerald-400/50 text-emerald-300",
  complex: "border-amber-400/50 text-amber-300",
};

function AssessmentsTab() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const load = useCallback(() => { api.get("/admin/assessments").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Delete this assessment?")) return; await api.delete(`/admin/assessments/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="assessments-tab">
      <Header title="Assessments" sub="Self-assessment submissions from the website. A copy of each is emailed to support." />
      <div className="px-8 pt-8" data-testid="admin-resources">
        <div className="overline mb-3" style={{ fontSize: "14px" }}>Free resource PDFs</div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {OUTCOMES.not_suitable.resources.map((r, i) => (
            <a key={r.file} href={r.file} download data-testid={`admin-resource-${i}`}
              className="border border-white/15 px-4 py-3 flex items-center justify-between gap-3 hover:bg-white/5 transition-colors text-sm">
              <span>{r.title}</span><Download className="w-4 h-4 shrink-0 text-[var(--txt-secondary)]" />
            </a>
          ))}
        </div>
      </div>
      <div className="p-8">
        <table className="w-full text-base">
          <thead><tr className="text-left overline border-b border-white/10" style={{ fontSize: "16px" }}>
            <th className="py-3">Date</th><th>Name</th><th>Organisation</th><th>Email</th><th>Result</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} data-testid={`assessment-row-${a.id}`} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-3 font-mono text-sm text-[var(--txt-secondary)]">{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="font-medium">{a.name}<div className="text-sm text-[var(--txt-muted)]">{a.role}</div></td>
                <td className="text-[var(--txt-secondary)]">{a.organisation || "—"}</td>
                <td className="text-[var(--txt-secondary)]">{a.email}</td>
                <td><span className={`inline-block px-2 py-0.5 text-sm border ${OUTCOME_STYLES[a.outcome]}`}>{a.outcome_title}</span></td>
                <td className="text-right space-x-1 whitespace-nowrap">
                  <button data-testid={`view-assessment-${a.id}`} onClick={() => setView(a)} className="px-2 py-1.5 border border-white/20 text-sm hover:bg-white/10 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> View</button>
                  <button data-testid={`delete-assessment-${a.id}`} onClick={() => del(a.id)} className="p-1.5 hover:bg-[var(--urgent)]/20 text-[var(--urgent)] inline-flex"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="py-8 text-[var(--txt-muted)]">No assessments yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={!!view} onClose={() => setView(null)} title={view ? `${view.name} — ${view.outcome_title}` : ""} testid="assessment-modal">
        {view && <AssessmentDetail a={view} />}
      </Modal>
    </div>
  );
}

const ASSESSMENT_LABELS = [
  ["size", "Organisation size"], ["cover", "Covering key people"], ["dependency", "External dependency"],
  ["disruption", "Disruption in last 5 years"], ["confidence", "Decision confidence"], ["collaboration", "Cross-team collaboration"],
  ["impact", "Who feels the impact"], ["interest", "Interest in an exercise"],
];

function AssessmentDetail({ a }) {
  return (
    <div className="space-y-5 text-base">
      <div className="text-[var(--txt-secondary)]">
        <div>{a.role}{a.organisation ? ` · ${a.organisation}` : ""}</div>
        <div><a className="underline" href={`mailto:${a.email}`}>{a.email}</a>{a.phone ? ` · ${a.phone}` : ""}</div>
        <div className="text-sm text-[var(--txt-muted)] mt-1">{new Date(a.created_at).toLocaleString()}</div>
      </div>
      <div>
        <div className="overline mb-2" style={{ fontSize: "14px" }}>Why this result</div>
        <ul className="list-disc pl-5 space-y-1 text-[var(--txt-secondary)]">{a.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
      </div>
      <div>
        <div className="overline mb-2" style={{ fontSize: "14px" }}>Answers</div>
        <dl className="divide-y divide-white/5">
          {ASSESSMENT_LABELS.map(([k, l]) => (
            <div key={k} className="py-2 flex justify-between gap-4"><dt className="text-[var(--txt-muted)]">{l}</dt><dd className="text-right">{Array.isArray(a.answers[k]) ? a.answers[k].join(", ") : a.answers[k]}</dd></div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ company_id: "", scenario_id: "", name: "", duration_minutes: 240, num_notetakers: 2 });
  const [commsSession, setCommsSession] = useState(null);
  const [reportsSession, setReportsSession] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/scenarios").then((r) => setScenarios(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { ...form, duration_minutes: parseInt(form.duration_minutes), num_notetakers: parseInt(form.num_notetakers) };
    try {
      if (editId) {
        await api.put(`/sessions/${editId}/config`, payload);
        toast.success("Session updated");
      } else {
        await api.post("/sessions", payload);
        toast.success("Session created");
      }
      setModal(false); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEdit = (s) => {
    setEditId(s.id);
    setForm({ company_id: s.company_id || "", scenario_id: s.scenario_id || "", name: s.name || "",
      trigger: s.trigger || "", duration_minutes: s.duration_minutes || 240, num_notetakers: s.num_notetakers || 2, breaks: s.breaks || [] });
    setModal(true);
  };
  const archive = async (id) => { await api.post(`/sessions/${id}/archive`); toast.success("Archived"); load(); };
  const duplicate = async (id) => { try { await api.post(`/sessions/${id}/duplicate`); toast.success("Session duplicated as a new draft"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Permanently delete all session data?")) return; await api.delete(`/sessions/${id}`); toast.success("Deleted"); load(); };

  const token = getToken();

  return (
    <div>
      <Header title="Sessions" sub="Configure, launch and control live simulations.">
        <Btn testid="new-session-btn" onClick={() => { setEditId(null); setForm({ company_id: companies[0]?.id || "", scenario_id: scenarios[0]?.id || "", name: "", trigger: "", duration_minutes: 240, num_notetakers: 2, breaks: [] }); setModal(true); }}>
          <Plus className="w-4 h-4" /> New session
        </Btn>
      </Header>
      <div className="p-8">
        <table className="w-full text-base">
          <thead><tr className="text-left overline border-b border-white/10" style={{ fontSize: "16px" }}>
            <th className="py-3">Session</th><th>Company</th><th>Scenario</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id} data-testid={`session-row-${s.id}`} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-3 font-medium">{s.name}</td>
                <td className="text-[var(--txt-secondary)]">{s.company_name}</td>
                <td className="text-[var(--txt-secondary)]">{s.scenario_title}</td>
                <td><StatusPill status={s.status} /></td>
                <td className="text-right space-x-1 whitespace-nowrap">
                  {s.status === "draft" && <button data-testid={`edit-session-${s.id}`} onClick={() => openEdit(s)} className="px-2 py-1.5 border border-white/20 text-sm hover:bg-white/10 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>}
                  <button data-testid={`control-session-${s.id}`} onClick={() => nav(`/sim/${s.id}`)} className="px-3 py-1.5 bg-white text-black text-sm font-medium hover:bg-gray-200 inline-flex items-center gap-1"><Settings className="w-3 h-3" /> Control</button>
                  <a data-testid={`csv-${s.id}`} href={`${API}/sessions/${s.id}/export.csv?token=${token}`} className="px-2 py-1.5 border border-white/20 text-sm hover:bg-white/10 inline-flex items-center gap-1"><FileDown className="w-3 h-3" /> CSV</a>
                  <a data-testid={`pdf-${s.id}`} href={`${API}/sessions/${s.id}/report.pdf?token=${token}`} className="px-2 py-1.5 border border-white/20 text-sm hover:bg-white/10 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Session PDF</a>
                  <button data-testid={`participant-reports-${s.id}`} onClick={() => setReportsSession(s)} className="px-2 py-1.5 border border-white/20 text-sm hover:bg-white/10 inline-flex items-center gap-1"><Users className="w-3 h-3" /> Participant reports</button>
                  <button data-testid={`duplicate-session-${s.id}`} onClick={() => duplicate(s.id)} className="p-1.5 hover:bg-white/10 inline-flex"><Copy className="w-3.5 h-3.5" /></button>
                  <button data-testid={`comms-session-${s.id}`} onClick={() => setCommsSession(s)} title="Send joining / follow-up emails" className="p-1.5 hover:bg-white/10 inline-flex"><Mail className="w-3.5 h-3.5" /></button>
                  <button data-testid={`archive-session-${s.id}`} onClick={() => archive(s.id)} className="p-1.5 hover:bg-white/10 inline-flex"><Archive className="w-3.5 h-3.5" /></button>
                  <button data-testid={`delete-session-${s.id}`} onClick={() => del(s.id)} className="p-1.5 hover:bg-[var(--urgent)]/20 text-[var(--urgent)] inline-flex"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {sessions.length === 0 && <tr><td colSpan={5} className="py-8 text-[var(--txt-muted)]">No sessions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={modal} onClose={() => { setModal(false); setEditId(null); }} title={editId ? "Edit session" : "New session"} testid="session-modal">
        <Input label="Session name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="session-name-input" placeholder="Q3 Cyber Exercise" />
        <label className="block"><span className="overline block mb-2" style={{ fontSize: "16px" }}>Company</span>
          <select data-testid="session-company-select" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className="w-full elevated px-3 py-2.5 text-base outline-none">
            <option value="" className="bg-[var(--bg-elevated)]">Select…</option>
            {companies.map((c) => <option key={c.id} value={c.id} className="bg-[var(--bg-elevated)]">{c.name}</option>)}
          </select>
        </label>
        <label className="block"><span className="overline block mb-2" style={{ fontSize: "16px" }}>Scenario</span>
          <select data-testid="session-scenario-select" value={form.scenario_id} onChange={(e) => setForm({ ...form, scenario_id: e.target.value })} className="w-full elevated px-3 py-2.5 text-base outline-none">
            <option value="" className="bg-[var(--bg-elevated)]">Select…</option>
            {scenarios.map((s) => <option key={s.id} value={s.id} className="bg-[var(--bg-elevated)]">{s.title}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Duration (min)" type="number" value={form.duration_minutes} onChange={(v) => setForm({ ...form, duration_minutes: v })} testid="session-duration-input" />
          <Input label="Note-takers" type="number" value={form.num_notetakers} onChange={(v) => setForm({ ...form, num_notetakers: v })} testid="session-notetakers-input" />
        </div>
        <div className="block">
          <span className="overline block mb-2" style={{ fontSize: "16px" }}>The trigger — scenario setting <span className="text-[var(--txt-muted)] normal-case font-normal">(optional)</span></span>
          <p className="text-sm text-[var(--txt-muted)] mb-2">Scene-setting briefing participants read before the timers begin. Guide: keep it under ~260 words. Leave blank to start straight into the first inject.</p>
          <RichText value={form.trigger || ""} onChange={(v) => setForm({ ...form, trigger: v })} testid="session-trigger" placeholder="It's 08:14 on a Monday morning. Reports are coming in that…" />
          <div className="text-right text-sm text-[var(--txt-muted)] font-mono mt-1" data-testid="trigger-word-count">
            {htmlToText(form.trigger).trim() ? htmlToText(form.trigger).trim().split(/\s+/).length : 0} / 260 words
          </div>
        </div>
        <BreaksEditor breaks={form.breaks} onChange={(b) => setForm({ ...form, breaks: b })} />
        <Btn testid="create-session-btn" onClick={save}>{editId ? "Save changes" : "Create session"}</Btn>
      </Modal>

      <SessionComms session={commsSession} onClose={() => setCommsSession(null)} />
      <ParticipantReports session={reportsSession} onClose={() => setReportsSession(null)} />
    </div>
  );
}

function ParticipantReports({ session, onClose }) {
  const [people, setPeople] = useState([]);
  const nav = useNavigate();
  const token = getToken();
  useEffect(() => {
    if (!session) return;
    setPeople([]);
    api.get(`/sessions/${session.id}/participants`).then((r) => setPeople(r.data)).catch(() => toast.error("Could not load participants"));
  }, [session]);
  if (!session) return null;
  return (
    <Modal open onClose={onClose} title={`Participant reports — ${session.name}`} testid="participant-reports-modal">
      <p className="text-sm text-[var(--txt-muted)]">Each report is a factual record of one person's participation. Participants can open their own once the session has ended.</p>
      <div className="divide-y divide-white/10 max-h-[60vh] overflow-y-auto">
        {people.map((p) => (
          <div key={p.id} data-testid={`report-row-${p.id}`} className="py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium truncate">{p.name || p.email}</div>
              <div className="text-sm text-[var(--txt-muted)] truncate">{p.sim_role || "No role"} · {p.responses} response{p.responses === 1 ? "" : "s"}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button data-testid={`view-report-${p.id}`} onClick={() => nav(`/report/${session.id}?user=${p.id}`)} className="px-2 py-1.5 border border-white/20 text-sm hover:bg-white/10 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> View</button>
              <a data-testid={`pdf-report-${p.id}`} href={`${API}/sessions/${session.id}/participant-report.pdf?user_id=${p.id}&token=${token}`} className="px-2 py-1.5 border border-white/20 text-sm hover:bg-white/10 inline-flex items-center gap-1"><Download className="w-3 h-3" /> PDF</a>
            </div>
          </div>
        ))}
        {people.length === 0 && <div className="py-6 text-[var(--txt-muted)]">No participants in this organisation yet.</div>}
      </div>
    </Modal>
  );
}

function SessionComms({ session, onClose }) {
  const [attendees, setAttendees] = useState([]);
  const [selected, setSelected] = useState({});
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyFollowup, setBusyFollowup] = useState(null);

  useEffect(() => {
    if (!session?.company_id) { setAttendees([]); return; }
    api.get(`/companies/${session.company_id}/users`).then((r) => {
      setAttendees(r.data);
      setSelected(Object.fromEntries(r.data.map((u) => [u.id, true])));
    }).catch(() => {});
  }, [session]);

  if (!session) return null;
  const selectedIds = attendees.filter((u) => selected[u.id]).map((u) => u.id);
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const sendJoining = async () => {
    if (busy) return;
    if (selectedIds.length === 0) return toast.error("Select at least one attendee");
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${session.id}/send-joining`, { user_ids: selectedIds, session_date: date, session_time: time });
      if (data.dev_mode) toast.message(`Dev mode — ${data.sent} joining email(s) logged (not sent). Add Mailjet keys to send for real.`, { duration: 8000 });
      else toast.success(`Joining instructions sent to ${data.results.filter((r) => r.delivered).length}/${data.sent} from ${data.from}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendFollowup = async (u) => {
    if (busyFollowup) return;
    setBusyFollowup(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/send-followup`, { session_id: session.id });
      if (data.dev_mode) toast.message(`Dev mode — follow-up for ${u.email} logged (not sent).`, { duration: 6000 });
      else if (data.delivered) toast.success(`Follow-up sent to ${u.email}`);
      else toast.error(`Could not send: ${data.error || "provider error"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyFollowup(null); }
  };

  return (
    <Modal open={!!session} onClose={onClose} title={`Comms — ${session.name}`} testid="session-comms-modal">
      <div className="space-y-2">
        <div className="overline" style={{ fontSize: "16px" }}>Joining instructions</div>
        <p className="text-sm text-[var(--txt-muted)]">Bulk-send to attendees below (from support@tardigradeteam.com). Untick anyone not attending on the day.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="overline block mb-1" style={{ fontSize: "13px" }}>Session date</span>
            <input data-testid="joining-date-input" value={date} onChange={(e) => setDate(e.target.value)} placeholder="12 August 2026" className="w-full elevated px-3 py-2 text-base outline-none" /></label>
          <label className="block"><span className="overline block mb-1" style={{ fontSize: "13px" }}>Start time</span>
            <input data-testid="joining-time-input" value={time} onChange={(e) => setTime(e.target.value)} placeholder="09:30 BST" className="w-full elevated px-3 py-2 text-base outline-none" /></label>
        </div>
      </div>

      <div className="elevated divide-y divide-white/5 max-h-64 overflow-auto" data-testid="session-comms-attendees">
        {attendees.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-3 py-2">
            <input type="checkbox" data-testid={`joining-check-${u.id}`} checked={!!selected[u.id]} onChange={() => toggle(u.id)} className="accent-white w-4 h-4" />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm text-[var(--txt-secondary)] truncate">{u.email}</div>
              <div className="text-xs text-[var(--txt-muted)]">{u.sim_role}</div>
            </div>
            <button data-testid={`send-followup-${u.id}`} onClick={() => sendFollowup(u)} disabled={busyFollowup === u.id} title="Send follow-up / debrief email (from support@)" className="text-sm border border-white/20 px-2 py-1 hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-40"><Mail className="w-3 h-3" /> Follow-up</button>
          </div>
        ))}
        {attendees.length === 0 && <div className="px-3 py-6 text-sm text-[var(--txt-muted)]">No attendees for this company yet.</div>}
      </div>

      <Btn testid="send-joining-btn" onClick={sendJoining}>{busy ? "Sending…" : `Send joining to ${selectedIds.length} selected`}</Btn>
    </Modal>
  );
}

function BreaksEditor({ breaks, onChange }) {
  const list = breaks || [];
  const update = (i, patch) => onChange(list.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const add = () => onChange([...list, { id: Math.random().toString(36).slice(2), label: "", mode: "offset", offset_minutes: 90, at_time: "12:30", duration_minutes: 15 }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="breaks-editor">
      <span className="overline block" style={{ fontSize: "16px" }}>Breaks &amp; reminders <span className="text-[var(--txt-muted)] normal-case font-normal">(facilitator only)</span></span>
      <p className="text-sm text-[var(--txt-muted)]">Pacing reminders for the day (e.g. lunch, coffee). These only prompt you in the control room — participants never see them. Set each as minutes after start or a clock time.</p>
      {list.map((b, i) => (
        <div key={b.id || i} className="elevated p-2 flex flex-wrap items-center gap-2" data-testid={`break-row-${i}`}>
          <input data-testid={`break-label-${i}`} value={b.label || ""} onChange={(e) => update(i, { label: e.target.value })} placeholder="Label (e.g. Lunch)" className="flex-1 min-w-[120px] elevated px-2 py-1.5 text-sm outline-none" />
          <select data-testid={`break-mode-${i}`} value={b.mode || "offset"} onChange={(e) => update(i, { mode: e.target.value })} className="elevated px-2 py-1.5 text-sm outline-none">
            <option value="offset" className="bg-[var(--bg-elevated)]">After start (min)</option>
            <option value="time" className="bg-[var(--bg-elevated)]">Clock time</option>
          </select>
          {b.mode === "time"
            ? <input data-testid={`break-time-${i}`} type="time" value={b.at_time || "12:30"} onChange={(e) => update(i, { at_time: e.target.value })} className="elevated px-2 py-1.5 text-sm outline-none" />
            : <input data-testid={`break-offset-${i}`} type="number" value={b.offset_minutes ?? 90} onChange={(e) => update(i, { offset_minutes: parseInt(e.target.value) || 0 })} className="w-20 elevated px-2 py-1.5 text-sm outline-none" />}
          <input data-testid={`break-duration-${i}`} type="number" value={b.duration_minutes ?? 15} onChange={(e) => update(i, { duration_minutes: parseInt(e.target.value) || 0 })} title="Length (min)" className="w-16 elevated px-2 py-1.5 text-sm outline-none" />
          <button type="button" data-testid={`break-remove-${i}`} onClick={() => remove(i)} className="p-1.5 hover:bg-[var(--urgent)]/20 text-[var(--urgent)]"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button type="button" data-testid="add-break-btn" onClick={add} className="text-sm border border-white/20 px-3 py-1.5 hover:bg-white/10 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add break</button>
    </div>
  );
}
